// RigSentinel Context Engine — Phase RCE-1
//
// ENGINEERING SESSION
// -------------------
// The current "Active Operation" that the user is working under. This is the
// single source of inheritance for every page in the app. Phase RCE-1 ships
// the storage layer + selectors + an "Active Operation" pill in the global
// header. Existing pages continue to use their own state — they will be
// migrated to consume the session in RCE-2 .. RCE-6.
//
// Storage key: `rsc.engSession.v1`.
//
// Session record shape (all fields optional — engine emits "" for any
// unknown member):
//   {
//     projectId, rigId, wellId, bhaId, drillStringId,
//     holeSection, currentOperation,
//     updatedAt,
//   }
//
// NOTE: dynamic snapshot values (currentMdFt, currentTvdFt, mudWeightInPpg)
// are NOT stored on the session — they live on the well / mud program. The
// session is small and stable on purpose; the resolver derives the live
// numbers from the underlying modules.

import { emit, RCE_EVENTS } from "./events";
import { scoped } from "@/lib/accountScope";

export const ENG_SESSION_STORAGE_KEY = "rsc.engSession.v1";
export const ENG_SESSION_SCHEMA_VERSION = 1;

const _now = () => new Date().toISOString();
const _safeParse = (s, f) => {
  try { return JSON.parse(s) ?? f; } catch { return f; }
};

export function emptyEngineeringSession() {
  return {
    schemaVersion: ENG_SESSION_SCHEMA_VERSION,
    projectId:        "",
    rigId:            "",
    wellId:           "",
    bhaId:            "",
    drillStringId:    "",
    mudProgramId:     "",
    holeSection:      "",
    currentOperation: "",
    updatedAt: null,
  };
}

/** Read the current Engineering Session, or null if none has been set. */
export function getEngineeringSession() {
  const raw = _safeParse(scoped.getItem(ENG_SESSION_STORAGE_KEY), null);
  if (!raw || typeof raw !== "object") return null;
  return { ...emptyEngineeringSession(), ...raw };
}

/** Has the user explicitly set an active session? */
export function hasActiveSession() {
  const s = getEngineeringSession();
  if (!s) return false;
  return Boolean(s.rigId || s.wellId || s.projectId || s.bhaId || s.drillStringId ||
                 s.currentOperation || s.holeSection);
}

/**
 * Replace the entire Engineering Session. Unknown keys are dropped to keep
 * the persisted shape clean. Emits ENGINEERING_SESSION_CHANGED on every
 * call, plus a fine-grained event for any individual member that changed.
 */
export function setEngineeringSession(patch) {
  const before = getEngineeringSession() || emptyEngineeringSession();
  const base = emptyEngineeringSession();
  const next = { ...base };
  // Whitelisted keys only — silently drop anything else.
  for (const k of Object.keys(base)) {
    if (k === "updatedAt" || k === "schemaVersion") continue;
    if (patch && Object.prototype.hasOwnProperty.call(patch, k)) {
      next[k] = patch[k] ?? "";
    } else {
      next[k] = before[k] ?? "";
    }
  }
  next.updatedAt = _now();
  scoped.setItem(ENG_SESSION_STORAGE_KEY, JSON.stringify(next));
  _emitDiffEvents(before, next);
  try { emit(RCE_EVENTS.ENGINEERING_SESSION_CHANGED, { before, after: next }); }
  catch { /* noop */ }
  return next;
}

/** Patch a single field (or several). Returns the new session. */
export function updateEngineeringSession(patch) {
  const before = getEngineeringSession() || emptyEngineeringSession();
  return setEngineeringSession({ ...before, ...(patch || {}) });
}

/** Clear the session entirely. */
export function clearEngineeringSession() {
  const before = getEngineeringSession();
  scoped.removeItem(ENG_SESSION_STORAGE_KEY);
  if (before) {
    try { emit(RCE_EVENTS.ENGINEERING_SESSION_CHANGED, { before, after: null }); }
    catch { /* noop */ }
  }
}

function _emitDiffEvents(before, after) {
  const safeEmit = (e, payload) => { try { emit(e, payload); } catch { /* noop */ } };
  if ((before.projectId || "") !== (after.projectId || ""))
    safeEmit(RCE_EVENTS.PROJECT_CHANGED, { id: after.projectId });
  if ((before.rigId || "") !== (after.rigId || ""))
    safeEmit(RCE_EVENTS.RIG_CHANGED, { id: after.rigId });
  if ((before.wellId || "") !== (after.wellId || ""))
    safeEmit(RCE_EVENTS.WELL_CHANGED, { id: after.wellId });
  if ((before.bhaId || "") !== (after.bhaId || ""))
    safeEmit(RCE_EVENTS.BHA_CHANGED, { id: after.bhaId });
  if ((before.drillStringId || "") !== (after.drillStringId || ""))
    safeEmit(RCE_EVENTS.DRILL_STRING_CHANGED, { id: after.drillStringId });
  if ((before.mudProgramId || "") !== (after.mudProgramId || ""))
    safeEmit(RCE_EVENTS.MUD_PROGRAM_CHANGED, { id: after.mudProgramId });
  if ((before.currentOperation || "") !== (after.currentOperation || ""))
    safeEmit(RCE_EVENTS.CURRENT_OPERATION_CHANGED, { value: after.currentOperation });
  if ((before.holeSection || "") !== (after.holeSection || ""))
    safeEmit(RCE_EVENTS.CURRENT_HOLE_SECTION_CHANGED, { value: after.holeSection });
}

/** Test helper — never called from app code. */
export function _clearEngineeringSession() {
  scoped.removeItem(ENG_SESSION_STORAGE_KEY);
}
